import Config from 'lib/Config';
import Constants from 'lib/Constants';
import { setGlobalCurrency } from 'lib/utils/currency';
import Button from 'components/shared/app/Button';
import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import FacebookService from 'services/providers/FacebookService';
import arrowIcon from 'static/icons/icon_arrow_blue_round.svg';
import SignupFbConnect from './components/SignupFbConnect';
import SignupGoals from './components/SignupGoals';
import SignupPayment from './components/SignupPayment';
import SignupProfile from './components/SignupProfile';
import SignupReview from './components/SignupReview';
import SignupSelectAccount from './components/SignupSelectAccount';
import './styles.scss';

const Signup = (props) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [signupState, setSignupState] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [fbToken, setFbToken] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [fbAccount, setFbAccount] = useState(null);
    const [fbUserId, setFbUserId] = useState(null);
    const [fbPixel, setFbPixel] = useState(null);
    const [profile, setProfile] = useState([]);
    const [goals, setGoals] = useState({});

    // redirect to main app if already logged in:
    useEffect(async () => {
        if (props.auth.authFinished && props.auth.loggedIn) {
            if (props.auth.loggedInProviders) {
                // if all logged in, go to route
                return navigate(Constants.DEFAULT_LOGIN_ROUTE);
            }
        }
    }, [props.auth.authFinished, props.auth.loggedIn, props.auth.loggedInProviders]);

    // Set the global currency symbol upon changes in the FB Account ID
    useEffect(async () => {
        if (fbAccount) {
            try {
                let { currency } = await FacebookService.getAccountMetadata(fbAccount);
                if (currency) {
                    setGlobalCurrency(currency);
                }
            } catch (error) {
                console.log("Error retrieving FB account currency:", error);
            }
        }
    }, [fbAccount]);

    const setSignupStep = (step) => {
        setSignupState(step);
        setError('');
    }

    const submitProfile = async (email, password, name, accountName) => {
        setProfile({ email, password, name, accountName });
        setSignupStep(3);
    }

    const submitGoals = async (goals) => {
        setGoals(goals);
        // skip payment method if we already have it (ie. they returned to review/edit)
        setSignupStep(5);
        //setSignupStep(paymentMethod ? 5 : 4);
    }

    const submitPaymentMethod = async (pd) => {
        setPaymentMethod(pd);
        setSignupStep(5);
    }

    const submitFinal = async () => {
        setLoading(true);
        props.auth.signup(profile, goals, paymentMethod, fbAccount, fbToken, fbPixel, fbUserId)
            .then(r => {
                console.log('Signup result:', r);
                setSignupStep(6);
            })
            .catch(e => {
                console.log('signup error', e)
                setError(e.error || 'An error occurred.');
            })
            .finally(() => {
                setLoading(false);
            });
    }


    const renderView = () => {
        switch (signupState) {

            case 0:
                return <SignupFbConnect
                    fbToken={fbToken}
                    setAccountsData={(accounts) => setAccounts(accounts)}
                    setFbToken={(token) => setFbToken(token)}
                    setFbUserId={(userId) => setFbUserId(userId)}
                    setSignupState={(s) => setSignupStep(s)}
                />

            case 1:
                return <SignupSelectAccount
                    fbAccount={fbAccount}
                    fbPixel={fbPixel}
                    accounts={accounts}
                    setSignupState={(s) => setSignupStep(s)}
                    setFbAccount={(a) => setFbAccount(a)}
                    setFbPixel={(p) => setFbPixel(p)}
                />

            case 2:
                return <SignupProfile
                    profile={profile}
                    error={error}
                    loading={loading}
                    setSignupState={(s) => setSignupStep(s)}
                    submitProfile={(email, password, name, r, a, c) => submitProfile(email, password, name, r, a, c)}
                />

            case 3:
                return <SignupGoals
                    goals={goals}
                    error={error}
                    loading={loading}
                    setSignupState={(s) => setSignupStep(s)}
                    submitGoals={(g) => submitGoals(g)}
                />

            case 4:
                return <SignupPayment
                    paymentMethod={paymentMethod}
                    error={error}
                    loading={loading}
                    setSignupState={(s) => setSignupStep(s)}
                    submitPaymentMethod={(s) => submitPaymentMethod(s)}
                />

            case 5:
                return <SignupReview
                    fbToken={fbToken} fbAccount={fbAccount} fbPixel={fbPixel} profile={profile} goals={goals} paymentMethod={paymentMethod}
                    error={error}
                    loading={loading}
                    setSignupState={(s) => setSignupStep(s)}
                    submit={() => submitFinal()}
                />

            case 6:
                return <div className="flex-col center">
                    <div className="margin lg">
                        <div className="font-plain-xlarge">Success!</div>
                        <div className="font-plain-large margin md">You may login to your account now.</div>
                    </div>
                    <Button link="/login" round simple bold filled center color="white" icon={<img src={arrowIcon} />} iconPosition="right" textPosition="center">Proceed to Login</Button>
                </div>

            default:
                return <React.Fragment />

        }
    }

    return (
        <div className="signup-container page-layout pad xl">
            <Helmet><title>{Config.SITE_TITLE_PREFIX} - Signup</title></Helmet>
            {renderView()}
        </div>
    )
}

export default Signup